import { Cards, ContactLink, L, LegalPageShell, P, type LegalDocument } from './LegalPage';

/**
 * LegalPrivacy — /legal/privacy
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * MAINTAINERS: PRE-COUNSEL DRAFT. Replace `PRIVACY_NOTICE` wholesale.
 *
 * WHY THIS IS WRITTEN THE WAY IT IS. The deployed instance carries real tenant
 * data, so an aspirational privacy policy would be actively harmful — it would
 * make commitments the code does not keep. Every factual claim below was checked
 * against the source before it was written:
 *
 *   Stripe        package.json `stripe`, @stripe/react-stripe-js; card + ACH via
 *                 src/routes/client-portal-pay.js (payment_method_types
 *                 ['card','us_bank_account']); metadata is record IDs only;
 *                 card fields are Stripe.js iframes (CSP frameSrc js.stripe.com
 *                 + hooks.stripe.com in src/app.js).
 *   QuickBooks    src/services/quickbooks/* — mapper.js clientToQboCustomer
 *                 sends display/company name, billing email, billing address;
 *                 invoiceToQboInvoice sends dates + line descriptions/amounts.
 *   Resend        src/services/email.js — inert unless RESEND_API_KEY resolves;
 *                 attachments ride through it (src/services/email-outbox.js).
 *   R2 / S3       src/services/document-storage.js — S3 SDK, R2 via S3_ENDPOINT;
 *                 falls back to base64-in-Postgres when unconfigured; object is
 *                 deleted with its row.
 *   Sentry        @sentry/node + @sentry/react; sendDefaultPii:false both sides.
 *   PostHog       src/services/analytics.js + web/src/lib/analytics.ts — inert
 *                 without a key; browser traffic proxied via same-origin /ingest.
 *   WorkOS        src/auth/workos.js — optional; demo instance uses email+password.
 *   Smarty        src/services/address-autocomplete.js — server-side passthrough,
 *                 inert without SMARTY_AUTH_ID/TOKEN, nothing stored.
 *   Gemini        src/services/ai/providers/gemini.js — document bytes ride as an
 *                 inline_data part to generativelanguage.googleapis.com. Anthropic
 *                 is the alternate provider (src/services/ai/providers/anthropic.js).
 *                 BOTH gates required (src/services/ai/index.js isAiEnabled).
 *   Google Fonts  web/src/styles/global.css @import fonts.googleapis.com.
 *   Cookie        src/app.js:378 — HttpOnly, Path=/, SameSite=Lax, Secure over
 *                 HTTPS; TTL from src/config/session.js (default 7 days).
 *   Audit log     src/db/schema.sql audit_logs — ip_address, user_agent columns.
 *   Lockout       users.failed_login_count / locked_at / status 'locked'.
 *
 * WHAT WAS DELIBERATELY NOT CLAIMED, because it is not in the code: any
 * certification (SOC 2 / ISO / HIPAA / PCI), penetration testing, encryption-at-
 * rest configuration beyond provider defaults, signed data-processing agreements
 * with the providers listed, and any retention, backup or deletion schedule.
 * Section 9 says so out loud. Do not "tidy" those admissions away.
 * ─────────────────────────────────────────────────────────────────────────────
 */
const PRIVACY_NOTICE: LegalDocument = {
  title: 'Privacy notice',
  summary:
    'What information AppealStack holds, where it goes, and what is protecting it — described as the software actually works today.',
  notice: (
    <>
      <strong style={{ display: 'block', marginBottom: 2 }}>
        This describes a demonstration instance.
      </strong>
      It is written to be accurate about the software as it stands rather than aspirational,
      so it says plainly where something is not implemented. It is not a production privacy
      policy, and it will be replaced in full by a counsel-reviewed policy before production
      use.
    </>
  ),
  intro: (
    <P>
      AppealStack is property-tax appeal management software for consulting firms and their
      clients. This notice covers the demonstration instance you are using now — see the{' '}
      <a href="/legal/demo" style={{ color: 'var(--color-hunter)' }}>demonstration notice</a>{' '}
      for what that means. Where the software has an option that is switched off in this
      deployment, that is said explicitly rather than left ambiguous.
    </P>
  ),
  sections: [
    {
      heading: 'Who is responsible for what',
      body: (
        <>
          <P>
            Two parties are involved, and it matters which one you need.
          </P>
          <L items={[
            <><strong>The consulting firm</strong> that gave you access decides which clients, properties and documents go into the system, and what it does with them. If you are a property owner using the client portal, the firm is your first point of contact for anything about your own records.</>,
            <><strong>We operate the platform</strong> — the application, its database and the infrastructure it runs on — and the service providers listed in section 7 are engaged by us, not by the firm.</>,
          ]} />
          <P>
            We are not attempting to characterise those roles under any particular privacy
            law here. Counsel will do that in the policy that replaces this notice.
          </P>
        </>
      ),
    },
    {
      heading: 'Information the firm puts into the system',
      body: (
        <>
          <P>
            Most of what the platform holds is entered or uploaded by the firm using it. In
            broad categories:
          </P>
          <L items={[
            <><strong>Client and parent-client records</strong> — business names, billing addresses, billing email addresses, engagement and fee terms.</>,
            <><strong>Contacts</strong> — names, titles, email addresses, phone numbers and mailing addresses of the people at those clients, and of assessors, collectors and other counterparties.</>,
            <><strong>Property, parcel and valuation records</strong> — addresses, legal descriptions, account numbers, assessed and market values, exemptions, and the appeal history attached to them.</>,
            <><strong>Money records</strong> — tax bills, invoices and invoice line items, fee and commission terms, payment amounts and status.</>,
            <><strong>Uploaded documents</strong> — notices of value, tax statements, operating statements, rent rolls, agent authorisations, correspondence and whatever else is attached to a record. These can contain sensitive commercial and personal information; the platform stores the file as given and does not inspect or restrict what is inside it.</>,
            <><strong>Free-text notes, messages and information requests</strong> exchanged between the firm and its clients.</>,
          ]} />
        </>
      ),
    },
    {
      heading: 'Information about your account',
      body: (
        <>
          <L items={[
            <><strong>Staff accounts</strong> — name, email address, role and permissions, and a password stored only as a bcrypt hash. Where single sign-on is used, an identifier from the identity provider instead.</>,
            <><strong>Client-portal accounts</strong> — email address, a bcrypt password hash, and which client records the account may see.</>,
            <><strong>A signature image</strong>, if you draw and save one in the portal. It is stored as an image so it can be re-used on forms you sign later, and it stays attached to your portal account.</>,
            <><strong>Sign-in outcomes</strong> — successful and failed sign-ins are recorded, and a run of failures locks the account.</>,
          ]} />
        </>
      ),
    },
    {
      heading: 'Information recorded automatically',
      body: (
        <>
          <L items={[
            <><strong>Audit log.</strong> Record changes are logged with the user who made them, the time, the IP address the request came from and the browser user-agent string. This is kept in the platform database and is visible to administrators of the organisation.</>,
            <><strong>Activity feed.</strong> Record-level events that drive the in-app timelines.</>,
            <><strong>Server logs.</strong> One line per request — method, path, status, duration and a request identifier. Request bodies are not logged.</>,
            <><strong>Product analytics</strong>, only when the deployment is configured with an analytics key. Events carry identifiers, counts, statuses and enumerated values; the code deliberately keeps personal information out of event properties, and analytics traffic is proxied through this site's own address rather than sent from your browser to the analytics vendor directly.</>,
            <><strong>Error reports</strong>, only when an error-monitoring key is configured. Stack traces and request context are sent; the vendor's "send personal information by default" setting is switched off in both the browser and the server.</>,
          ]} />
        </>
      ),
    },
    {
      heading: 'Cookies',
      body: (
        <>
          <L items={[
            <><strong>One cookie is required to use the product.</strong> <code>appealstack_session</code> holds an opaque session token. It is set <code>HttpOnly</code> (page scripts cannot read it) and <code>SameSite=Lax</code>, is marked <code>Secure</code> when the site is served over HTTPS, and expires after seven days by default. Signing out clears it and revokes the session on the server.</>,
            <><strong>Analytics cookies</strong> are set by the product-analytics library, and only when analytics is switched on for the deployment.</>,
            <><strong>No advertising or cross-site tracking cookies</strong> are used, and no advertising networks are integrated.</>,
          ]} />
        </>
      ),
    },
    {
      heading: 'Payments',
      body: (
        <>
          <P>
            Clients can pay invoices by card or by US bank transfer (ACH) through the client
            portal. Payment is handled by Stripe.
          </P>
          <L items={[
            'Card numbers and bank account details are typed into fields hosted by Stripe and displayed inside the page. Those details go directly to Stripe. They do not pass through, and are not stored on, AppealStack servers.',
            'What we send Stripe alongside the payment is record identifiers — the invoice, organisation, client and portal-user ids — and the amount. Not names, not addresses.',
            'What we store back is the amount, currency, status and the Stripe reference for the payment, so an invoice can be reconciled.',
          ]} />
        </>
      ),
    },
    {
      heading: 'Service providers',
      body: (
        <>
          <P>
            The following third parties receive information in the course of running the
            platform. Several are optional and are inert unless the deployment is configured
            with credentials for them; where that is the case it is stated, because "the
            integration exists in the code" and "your data is going there" are different
            claims.
          </P>
          <Cards items={[
            {
              name: 'Stripe — payments',
              body: 'Card and ACH payments from the client portal, as described above. Active only when the deployment is configured with Stripe credentials.',
            },
            {
              name: 'Intuit QuickBooks Online — accounting sync',
              body: 'Optional, and only after a firm connects its own QuickBooks account. Sends the client name, billing email and billing address as a QuickBooks customer, and invoice dates with line-item descriptions and amounts as a QuickBooks invoice.',
            },
            {
              name: 'Resend — outbound email',
              body: 'Invoice delivery, portal notifications, information requests and password setup. Recipient addresses, message content and any attached documents pass through Resend. Inert unless an email key is configured.',
            },
            {
              name: 'Cloudflare R2 (or Amazon S3) — document storage',
              body: 'Uploaded document files are stored as objects. Which store is used depends on deployment configuration; this deployment is configured for Cloudflare R2. Without storage credentials the platform falls back to keeping the file bytes in its own database instead.',
            },
            {
              name: 'Sentry — error monitoring',
              body: 'Errors, stack traces and request context from the browser and the server. Inert unless an error-monitoring key is configured.',
            },
            {
              name: 'PostHog — product analytics',
              body: 'Usage events as described in section 4. Inert unless an analytics key is configured.',
            },
            {
              name: 'WorkOS — single sign-on',
              body: 'Optional. Handles the sign-in exchange with a firm’s identity provider and returns an identifier and email address. This demonstration instance signs in with email and password instead.',
            },
            {
              name: 'Smarty — US address autocomplete',
              body: 'When you type into an address field, the partial address is sent to Smarty through our server (never from your browser directly) and suggestions come back. Nothing is stored. Inert unless address credentials are configured.',
            },
            {
              name: 'OpenStreetMap Foundation (Nominatim) — geocoding',
              body: 'To place a property on the map, its street address is sent through our server to the OpenStreetMap Foundation’s public Nominatim service and coordinates come back. This is the default provider and runs without any configuration; when a Mapbox key is configured, Mapbox is used instead.',
            },
            {
              name: 'Google or Anthropic — AI document reading',
              body: 'Switched off by default. See section 8 for exactly what is sent when it is on.',
            },
            {
              name: 'Google Fonts',
              body: 'The interface loads its typefaces from Google’s font service, so your browser’s IP address and user-agent are visible to Google when a page loads.',
            },
            {
              name: 'Railway — hosting',
              body: 'The application, its PostgreSQL database and its job queue run on Railway infrastructure. All stored records live there.',
            },
          ]} />
        </>
      ),
    },
    {
      heading: 'AI document reading',
      body: (
        <>
          <P>
            The platform can read an uploaded document with an AI model to pull values out of
            it. This is worth describing precisely, because it means a document leaves the
            platform.
          </P>
          <L items={[
            <><strong>It is off by default and requires two separate switches.</strong> The deployment must be configured with an AI provider and key, and the firm's organisation must have AI reading enabled. If either is off, no model is contacted and no document leaves.</>,
            <><strong>When it is on, the document file itself is uploaded to the model provider</strong> — the whole PDF or image, not a summary or an extract — and a structured reading comes back. The configured provider is either Google (Gemini) or Anthropic.</>,
            <><strong>What that provider does with the content is governed by its own terms</strong>, not by this notice. We do not control it.</>,
            <><strong>Nothing read by a model is written to a record unattended.</strong> Extracted values land as staged rows that a person reviews and accepts, the same gate a spreadsheet import goes through.</>,
          ]} />
          <P>
            If it matters to you whether AI reading is on for your firm, ask — do not assume
            either answer from this page.
          </P>
        </>
      ),
    },
    {
      heading: 'How information is protected, and what we are not claiming',
      body: (
        <>
          <P>What is implemented today:</P>
          <L items={[
            'Passwords are stored as bcrypt hashes and are never held in readable form.',
            'The session cookie is HttpOnly and SameSite=Lax, marked Secure over HTTPS, and expires on the server as well as in the browser.',
            'Repeated failed sign-ins lock the account, and both successful and failed attempts are recorded.',
            'Every record is scoped to an organisation, and permission checks run on the server rather than only in the browser — including the rules that hide financial figures from roles that should not see them, on writes as well as reads.',
            'Record changes are written to an audit log with the user, time, IP address and browser.',
            'API traffic is rate limited, standard security headers and a content-security policy are applied, and the site is served over HTTPS.',
          ]} />
          <P>What we are <em>not</em> claiming, because it is not in place:</P>
          <L items={[
            'No SOC 2, ISO 27001, HIPAA or PCI certification, and no third-party security audit.',
            'No formal penetration test.',
            'No negotiated data-processing agreements with the providers in section 7 beyond their standard published terms.',
            'Encryption at rest is whatever the hosting and storage providers apply by default. We have not configured customer-managed keys and are not representing a specific standard.',
            'No guaranteed backup, restore or breach-notification timeline for this environment.',
          ]} />
        </>
      ),
    },
    {
      heading: 'How long information is kept',
      body: (
        <>
          <L items={[
            'No automatic retention or deletion schedule is implemented. Records stay until somebody deletes them.',
            'Deleting a document removes the stored file object along with its record, so the file does not outlive its metadata.',
            'Because this is a demonstration instance, the whole environment may be reset or rebuilt at any time. Do not rely on anything here persisting.',
          ]} />
        </>
      ),
    },
    {
      heading: 'Requests about your information',
      body: (
        <>
          <P>
            If you reached the platform through a consulting firm's client portal, ask that
            firm first — the records are theirs to explain, correct or remove, and they can
            do it from inside the product.
          </P>
          <P>
            Otherwise write to <ContactLink />. Be aware that this environment has no
            automated export or erasure tooling; requests are handled by hand, and what we
            can do for records that belong to a firm depends on that firm.
          </P>
        </>
      ),
    },
    {
      heading: 'Who this is for',
      body: (
        <P>
          AppealStack is business software for property-tax professionals and their clients.
          It is not directed at children, and accounts are not knowingly created for anyone
          under 18.
        </P>
      ),
    },
    {
      heading: 'Changes to this notice',
      body: (
        <>
          <P>
            This notice is dated at the top of the page. It describes a demonstration
            instance and will change as the software does.
          </P>
          <P>
            Before AppealStack is used in production for real client work, this notice will
            be replaced in full by a privacy policy reviewed by counsel, alongside a written
            agreement. Questions about either can go to <ContactLink />.
          </P>
        </>
      ),
    },
  ],
};

export function LegalPrivacy() {
  return (
    <LegalPageShell
      doc={PRIVACY_NOTICE}
      otherHref="/legal/demo"
      otherLabel="Demonstration notice"
    />
  );
}
